import type { GithubChoice, Item } from '@hugo/core'
import { CheckCircle2Icon, FlaskConicalIcon, HouseIcon } from 'lucide-react'
import { useEffect, useSyncExternalStore } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Kbd } from '@/components/ui/kbd'
import { useConnectors, useItemDetail } from '@/lib/items'

/**
 * Marking a GitHub task done asks what GitHub should do (decision 108): move it to the Testing
 * column of its Projects board, close the issue, or nothing (only in hugo). The last choice is the
 * default. `askGithubDone` opens the one dialog (mounted in the app shell) and resolves with the
 * choice, or null when the user cancels (the item then stays open).
 */
type Pending = { item: Pick<Item, 'id' | 'title'>; resolve: (choice: GithubChoice | null) => void }

let pending: Pending | null = null
const listeners = new Set<() => void>()
const emit = () => {
  for (const listener of listeners) listener()
}

export function askGithubDone(item: Pick<Item, 'id' | 'title'>): Promise<GithubChoice | null> {
  // A second question replaces the first, which counts as cancelled.
  pending?.resolve(null)
  return new Promise((resolve) => {
    pending = { item, resolve }
    emit()
  })
}

function answer(choice: GithubChoice | null) {
  const current = pending
  pending = null
  emit()
  current?.resolve(choice)
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Whether marking this item done asks about GitHub: a GitHub issue or PR filed as a task. */
export const asksGithub = (item: Pick<Item, 'source' | 'kind'>) =>
  item.source === 'github' && item.kind === 'task'

const CHOICES: { choice: GithubChoice; key: string; label: string; icon: typeof HouseIcon }[] = [
  { choice: 'testing', key: 'T', label: 'Move to Testing', icon: FlaskConicalIcon },
  { choice: 'close', key: 'C', label: 'Close the issue', icon: CheckCircle2Icon },
  { choice: 'hugo', key: 'H', label: 'Only in hugo', icon: HouseIcon },
]

export function GithubDoneDialog() {
  const current = useSyncExternalStore(subscribe, () => pending)
  const connectors = useConnectors()
  const connection = connectors.data?.connectors.find((c) => c.id === 'github')?.connection
  const options = connection?.github
  const testing = options?.testingStatus ?? 'Testing'
  // Without access to Projects boards there is no Testing column to move it to.
  const noBoards = connection?.missingScopes.includes('project') ?? false
  // A pull request is never closed by hugo: its link says which it is.
  const details = useItemDetail(current?.item.id ?? null)
  const pr = details.data?.sources.some(
    (s) => s.connector === 'github' && s.url?.includes('/pull/'),
  )
  const offered = CHOICES.filter(
    ({ choice }) => !(choice === 'close' && pr) && !(choice === 'testing' && noBoards),
  )
  const last = options?.lastChoice ?? 'hugo'
  const fallback: GithubChoice = offered.some((c) => c.choice === last) ? last : 'hugo'

  useEffect(() => {
    if (!current) return
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const hit = offered.find((c) => c.key.toLowerCase() === event.key.toLowerCase())
      if (!hit) return
      // Before the app's own shortcuts (C would also open Capture).
      event.preventDefault()
      event.stopPropagation()
      answer(hit.choice)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [current, offered])

  return (
    <Dialog open={current !== null} onOpenChange={(open) => (open ? null : answer(null))}>
      <DialogContent className="sm:max-w-md" data-testid="github-done-dialog">
        <DialogHeader>
          <DialogTitle>Done in GitHub too?</DialogTitle>
          <DialogDescription className="break-words">{current?.item.title}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {offered.map(({ choice, key, label, icon: Icon }) => (
            <Button
              key={choice}
              variant={choice === fallback ? 'default' : 'outline'}
              className="h-11 justify-between sm:h-10"
              // The default (last choice) has the focus: Enter takes it.
              autoFocus={choice === fallback}
              data-choice={choice}
              onClick={() => answer(choice)}
            >
              <span className="flex items-center gap-2">
                <Icon aria-hidden="true" />
                {choice === 'testing' ? `Move to ${testing}` : label}
              </span>
              <Kbd>{key}</Kbd>
            </Button>
          ))}
        </div>
        {noBoards ? (
          <p className="text-xs text-muted-foreground" data-testid="github-done-no-boards">
            To move issues to {testing}, connect GitHub again in Settings → Apps.
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Undo puts GitHub back too. Esc keeps the task open.
        </p>
      </DialogContent>
    </Dialog>
  )
}
